import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Award, Loader2, Trophy, Target, BookOpen, Smile, Heart, Mic, Star, Feather, Crown, Download, X, Calendar } from 'lucide-react';
import axios from 'axios';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  trophy: Trophy, crown: Crown, target: Target, book: BookOpen,
  feather: Feather, smile: Smile, heart: Heart, mic: Mic, star: Star,
};

const COLOR_MAP = {
  trophy: { bg: 'bg-violet-100 dark:bg-violet-950', text: 'text-violet-600 dark:text-violet-400', accent: '#7c3aed', gradient: 'from-violet-600 to-purple-700' },
  crown: { bg: 'bg-amber-100 dark:bg-amber-950', text: 'text-amber-600 dark:text-amber-400', accent: '#d97706', gradient: 'from-amber-500 to-orange-600' },
  target: { bg: 'bg-emerald-100 dark:bg-emerald-950', text: 'text-emerald-600 dark:text-emerald-400', accent: '#059669', gradient: 'from-emerald-500 to-teal-600' },
  book: { bg: 'bg-blue-100 dark:bg-blue-950', text: 'text-blue-600 dark:text-blue-400', accent: '#2563eb', gradient: 'from-blue-500 to-indigo-600' },
  feather: { bg: 'bg-indigo-100 dark:bg-indigo-950', text: 'text-indigo-600 dark:text-indigo-400', accent: '#4f46e5', gradient: 'from-indigo-500 to-violet-600' },
  smile: { bg: 'bg-yellow-100 dark:bg-yellow-950', text: 'text-yellow-600 dark:text-yellow-400', accent: '#ca8a04', gradient: 'from-yellow-500 to-amber-600' },
  heart: { bg: 'bg-rose-100 dark:bg-rose-950', text: 'text-rose-600 dark:text-rose-400', accent: '#e11d48', gradient: 'from-rose-500 to-pink-600' },
  mic: { bg: 'bg-sky-100 dark:bg-sky-950', text: 'text-sky-600 dark:text-sky-400', accent: '#0284c7', gradient: 'from-sky-500 to-blue-600' },
  star: { bg: 'bg-amber-100 dark:bg-amber-950', text: 'text-amber-600 dark:text-amber-400', accent: '#d97706', gradient: 'from-amber-500 to-orange-600' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
}

function PrintableCertificate({ cert, onClose }) {
  const accent = COLOR_MAP[cert.icon]?.accent || '#1e3a5f';
  const isProgram = cert.type === 'program';
  const duration = cert.program_duration;
  const startDate = formatDate(cert.started_at);
  const completedDate = formatDate(cert.completed_at || cert.earned_at);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>Livon Certifikat — ${cert.title}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Inter:wght@300;400;500;600&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f0f0; font-family: 'Inter', sans-serif; }
        .page { width: 800px; height: 566px; background: white; position: relative; overflow: hidden; }
        
        /* Border frame */
        .frame { position: absolute; inset: 12px; border: 2px solid ${accent}30; }
        .frame-inner { position: absolute; inset: 4px; border: 1px solid ${accent}15; }
        
        /* Top accent bar */
        .topbar { position: absolute; top: 0; left: 0; right: 0; height: 6px; background: linear-gradient(90deg, ${accent}, ${accent}cc); }
        .bottombar { position: absolute; bottom: 0; left: 0; right: 0; height: 6px; background: linear-gradient(90deg, ${accent}cc, ${accent}); }
        
        /* Corner decorations */
        .corner { position: absolute; width: 40px; height: 40px; }
        .corner svg { width: 100%; height: 100%; fill: ${accent}15; }
        .c-tl { top: 24px; left: 24px; } .c-tr { top: 24px; right: 24px; transform: rotate(90deg); }
        .c-bl { bottom: 24px; left: 24px; transform: rotate(270deg); } .c-br { bottom: 24px; right: 24px; transform: rotate(180deg); }
        
        .content { position: relative; z-index: 1; padding: 50px 70px; text-align: center; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; }
        .brand { font-family: 'Playfair Display', serif; font-size: 13px; letter-spacing: 8px; text-transform: uppercase; color: ${accent}; font-weight: 600; }
        .type { font-size: 10px; letter-spacing: 4px; text-transform: uppercase; color: #999; margin-top: 4px; }
        .sep { width: 60px; height: 2px; background: ${accent}; margin: 20px auto; }
        .title { font-family: 'Playfair Display', serif; font-size: ${isProgram ? '36px' : '40px'}; font-weight: 700; color: #1a1a1a; line-height: 1.2; }
        .desc { font-size: 16px; color: #666; margin-top: 10px; font-weight: 300; max-width: 500px; line-height: 1.5; }
        .details { display: flex; gap: 30px; justify-content: center; margin-top: 16px; }
        .detail { font-size: 12px; color: #888; }
        .detail strong { color: #555; font-weight: 500; }
        .sep2 { width: 40px; height: 1px; background: ${accent}40; margin: 20px auto; }
        .name { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 600; color: #1a1a1a; }
        .date { font-size: 12px; color: #999; margin-top: 6px; }
        .footer { font-size: 9px; color: #bbb; margin-top: 20px; letter-spacing: 2px; text-transform: uppercase; }
        
        @media print { 
          body { background: white; }
          @page { size: landscape; margin: 0; }
        }
      </style>
    </head><body>
      <div class="page">
        <div class="topbar"></div>
        <div class="bottombar"></div>
        <div class="frame"><div class="frame-inner"></div></div>
        <div class="corner c-tl"><svg viewBox="0 0 40 40"><path d="M0 0 L40 0 L40 3 L3 3 L3 40 L0 40 Z"/></svg></div>
        <div class="corner c-tr"><svg viewBox="0 0 40 40"><path d="M0 0 L40 0 L40 3 L3 3 L3 40 L0 40 Z"/></svg></div>
        <div class="corner c-bl"><svg viewBox="0 0 40 40"><path d="M0 0 L40 0 L40 3 L3 3 L3 40 L0 40 Z"/></svg></div>
        <div class="corner c-br"><svg viewBox="0 0 40 40"><path d="M0 0 L40 0 L40 3 L3 3 L3 40 L0 40 Z"/></svg></div>
        <div class="content">
          <div class="brand">Livon</div>
          <div class="type">Certifikat</div>
          <div class="sep"></div>
          <div class="title">${cert.title}</div>
          <div class="desc">${cert.description}</div>
          ${isProgram && duration ? `<div class="details">
            <div class="detail"><strong>${duration} dagar</strong> genomfört</div>
            ${startDate ? `<div class="detail">Startad <strong>${startDate}</strong></div>` : ''}
            <div class="detail">Avslutad <strong>${completedDate}</strong></div>
          </div>` : ''}
          <div class="sep2"></div>
          <div class="name">${cert.user_name}</div>
          <div class="date">Utfärdat ${completedDate}</div>
          <div class="footer"></div>
        </div>
      </div>
      <script>window.onload = function() { window.print(); }</script>
    </body></html>`);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in" data-testid="certificate-preview">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl">
        <button onClick={onClose} className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-white dark:bg-card shadow-lg flex items-center justify-center hover:scale-105 transition-transform" data-testid="close-cert-preview">
          <X className="w-5 h-5" />
        </button>

        {/* Certificate Preview */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-2xl" style={{ border: `2px solid ${accent}30` }}>
          <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}cc)` }} />
          <div className="p-8 md:p-12 text-center">
            <p className="text-xs tracking-[0.5em] uppercase font-semibold" style={{ color: accent }}>Livon</p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mt-1">Certifikat</p>
            
            <div className="w-12 h-0.5 mx-auto my-5" style={{ background: accent }} />
            
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">{cert.title}</h2>
            <p className="text-gray-500 mt-3 text-base max-w-md mx-auto leading-relaxed">{cert.description}</p>
            
            {isProgram && (
              <div className="flex items-center justify-center gap-6 mt-4 text-sm text-gray-400">
                {duration > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {duration} dagar
                  </span>
                )}
                {startDate && <span>Start: {startDate}</span>}
                <span>Klar: {completedDate}</span>
              </div>
            )}
            
            <div className="w-8 h-px mx-auto my-5" style={{ background: `${accent}40` }} />
            
            <p className="font-heading text-2xl font-semibold text-gray-900">{cert.user_name}</p>
            <p className="text-sm text-gray-400 mt-1">Utfärdat {completedDate}</p>
          </div>
          <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${accent}cc, ${accent})` }} />
        </div>

        <div className="flex justify-center mt-5">
          <Button onClick={handlePrint} className="rounded-full px-8 h-12 text-base shadow-lg" data-testid="print-cert-btn">
            <Download className="w-5 h-5 mr-2" />
            Ladda ner / Skriv ut
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CertificatesPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCerts, setNewCerts] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);

  useEffect(() => { fetchCertificates(); }, []);

  const fetchCertificates = async () => {
    try {
      const checkRes = await axios.get(`${API}/certificates/check`, { headers: getAuthHeader() });
      setNewCerts(checkRes.data.new_certificates || []);
      const res = await axios.get(`${API}/certificates/my`, { headers: getAuthHeader() });
      setCertificates(res.data.certificates || []);
    } catch (e) {
      console.error('Could not load certificates');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="certificates-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Tillbaka
          </Button>
          <h1 className="font-heading text-xl">Certifikat</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
            <Award className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="font-heading text-2xl mb-2">Dina certifikat</h2>
          <p className="text-muted-foreground text-sm">Tryck för att förhandsgranska och ladda ner</p>
        </div>

        {newCerts.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border border-amber-200/50 dark:border-amber-800/30 rounded-2xl p-5 animate-fade-in">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">Nytt certifikat!</p>
            {newCerts.map(cert => (
              <p key={cert.id} className="text-sm text-amber-700 dark:text-amber-400">{cert.title} — {cert.description}</p>
            ))}
          </div>
        )}

        {certificates.length === 0 ? (
          <div className="text-center py-16 animate-fade-in">
            <Trophy className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground mb-2">Inga certifikat ännu</p>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Klara program, uppnå mål och skriv dagbok för att tjäna dina första diplom!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {certificates.map((cert, i) => {
              const IconComp = ICON_MAP[cert.icon] || Award;
              const colors = COLOR_MAP[cert.icon] || COLOR_MAP.trophy;
              const isProgram = cert.type === 'program';
              return (
                <button
                  key={cert.id}
                  onClick={() => setSelectedCert(cert)}
                  className="w-full text-left bg-card rounded-2xl p-5 border border-border/30 hover:border-primary/30 hover:shadow-lg transition-all animate-slide-up group"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  data-testid={`certificate-${cert.achievement_id || cert.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl ${colors.bg} ${colors.text} flex items-center justify-center flex-shrink-0`}>
                      <IconComp className="w-7 h-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-base font-semibold truncate">{cert.title}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5 truncate">{cert.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        {isProgram && cert.program_duration > 0 && (
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{cert.program_duration} dagar</span>
                        )}
                        <span>{formatDate(cert.earned_at)}</span>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-muted-foreground/30 group-hover:text-primary transition-colors shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
      <Footer />

      {selectedCert && <PrintableCertificate cert={selectedCert} onClose={() => setSelectedCert(null)} />}
    </div>
  );
}
